import L from 'leaflet';
import 'leaflet-realtime';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import ReactDOMServer from 'react-dom/server';

import {
    COLORS_PROJECT,
    ENDPOINT_DATA_GEOSERVICIOS,
    GIS_POINT_ICON_SIZE_PIXELS,
    GIS_SOUND_ALERT_MAXIMUM_DURATION_SECONDS,
    PointColors,
    REALTIME_INTERVAL_SECONDS_U
} from '../../../constants';
import { dataGeoServiciosFetch } from '../../../helpers/fetch';
import ShapePoint from '../icons/ShapePoint';
import ShapesTooltip from '../tooltips/ShapesTooltip';
import { sendGeoPointAndGetInfo } from '../../../actions/gis';
import { id } from 'chartjs-plugin-streaming';


const { color_shape_one, color_shape_two, color_shape_three, color_shape_zero, color_shape_h } = COLORS_PROJECT;

/**
 * Get radius circle
 * @param {Number} idCliente id of client
 * @param {() => any} playSoundAlert - Function that plays sound alert, a sound alert needs is played when a point meets
 * certain conditions.
 * @param {() => any} stopSoundAlert - Function that stops sound alert, a sound alert needs is stopped when user clicks
 * a button or after a certain amount of time.
 * @param {String} colorPoints Color circle
 * @returns {Array}Array of two elements: shapes shapesLayerGroup and shapes realtime reference, the reference needs to be returned
 */
const getShapesLayerGroupRealTime = (
  idCliente,
  playSoundAlert,
  stopSoundAlert,
  dispatch
) => {
  const shapesLayerGroup = new L.LayerGroup();
  const realtimeReference = L.realtime(
    {
      url: ENDPOINT_DATA_GEOSERVICIOS,
      method: 'POST',
      body: JSON.stringify({
        solicitud: 'shpCliente',
        idCliente: idCliente,
      }),
    },
    {
      container: shapesLayerGroup,
      interval: REALTIME_INTERVAL_SECONDS_U * 1000,
      onEachFeature: ({ properties: { nivel, propiedades } }, layer) => {
        layer.bindTooltip(
          ReactDOMServer.renderToString(
            <ShapesTooltip level={nivel} properties={propiedades} />
          ),
          { direction: 'top' }
        );
        layer.on('click', () => {
          console.log(layer.feature.properties.id);
          dispatch(sendGeoPointAndGetInfo({pointID: layer.feature.properties.id}))
        });
      },
      pointToLayer: (feature, latlng) =>
        L.marker(latlng, {
          icon: createPointIcon(feature),
          pmIgnore: true,
        }),
      style: (feature) => ({
        color: getShapeColor(feature),
        opacity: 0.5,
      }),
      removeMissing: true,
      updateFeature: (feature, oldLayer, newLayer) => {
        if (
          feature.properties.nivel !== 0 &&
          (!oldLayer ||
            feature.properties.nivel !== oldLayer.feature.properties.nivel)
        ) {
          if (!oldLayer) {
            /*
                Leaves 5 seconds for user to interact with the application.
                
                This is because if the user hasn't interacted with the application,
                the browser won't allow sounds to be played.
                */
            setTimeout(() => {
              playSoundAlert();
            }, [5000]);
          } else {
            playSoundAlert();
          }
          setTimeout(() => {
            stopSoundAlert();
          }, [GIS_SOUND_ALERT_MAXIMUM_DURATION_SECONDS * 1000]);
        }
        return newLayer;
      },
    }
  );
  return [shapesLayerGroup, realtimeReference];
};

// const pointID = 1248
// const handleSendGeo = dispatch(sendGeoPointAndGetInfo({pointID}))
const getShapesLayerGroupHistoric = async (parameters) => {
  const shapesStyle = {
    color: color_shape_h,
  };
  const shapesGeoJson = await dataGeoServiciosFetch(parameters, 'POST');
  const shapes = shapesGeoJson.features.map((feature) => {
    const {
      properties: { nivel, propiedades },
    } = feature;
    return L.geoJSON(shapesGeoJson, {
      style: shapesStyle,
      pointToLayer: (feature, latlng) =>
        L.marker(latlng, {
          icon: L.divIcon({
            className: '',
            html: ReactDOMServer.renderToString(
              <ShapePoint
                fillColor={color_shape_h}
                fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
                strokeColor={PointColors[3].stroke}
              />
            ),
            iconSize: [GIS_POINT_ICON_SIZE_PIXELS, GIS_POINT_ICON_SIZE_PIXELS],
            iconAnchor: [
              GIS_POINT_ICON_SIZE_PIXELS / 2,
              GIS_POINT_ICON_SIZE_PIXELS,
            ],
            tooltipAnchor: [0, -GIS_POINT_ICON_SIZE_PIXELS],
          }),
          pmIgnore: true,
        }),
    }).bindTooltip(
      ReactDOMServer.renderToString(
        <ShapesTooltip level={nivel} properties={propiedades} />
      )
    );
  });
  const shapesLayerGroup = new L.LayerGroup(shapes);
  return shapesLayerGroup;
};

/**
 * Colors for the shapes
 * @param {object} feature - Object with level property
 * @param {string} color - Color selector for the points
 * @param {string} colorPoints - Color selector for the points
 * @returns {String} color based on "nivel" property.
 */
const getShapeColor = (feature) => {
  //if (feature.properties.nivel === 1 || feature.properties.nivel === 3) {
  if (feature.properties.nivel === 1) {
    return color_shape_one;
  } else if (feature.properties.nivel === 2) {
    return color_shape_two;
  } else if (feature.properties.nivel === 3) {
    return color_shape_three;
  } else if (feature.properties.nivel === 0) {
    return color_shape_zero;
  } else {
    return '#000000';
  }
};

/**
 * Create Point icon in GIS
 * @param {*} feature - GeoJSON feature, the point varies depending on the feature properties.
 * @returns Point icon.
 */
const createPointIcon = (feature) => {
  return L.divIcon({
    className: '',
    html: ReactDOMServer.renderToString(createPointHtml(feature)),
    iconSize: [GIS_POINT_ICON_SIZE_PIXELS, GIS_POINT_ICON_SIZE_PIXELS],
    iconAnchor: [GIS_POINT_ICON_SIZE_PIXELS / 2, GIS_POINT_ICON_SIZE_PIXELS],
    tooltipAnchor: [0, -GIS_POINT_ICON_SIZE_PIXELS],
  });
};

/**
 * Create Point with different color
 * @param {*} feature - GeoJSON feature, the point varies depending on the feature properties.
 * @returns Point React component, ready to be converted to HTML.
 */
const createPointHtml = (feature) => {
  if (feature.properties.nivel === 1) {
    return (
      <ShapePoint
        fillColor={color_shape_one}
        fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
        strokeColor={PointColors[3].stroke}
      />
    );
  } else if (feature.properties.nivel === 0) {
    return (
      <ShapePoint
        fillColor={color_shape_two}
        fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`}
        strokeColor={PointColors[2].stroke}
      />
    );
  } else {
    return <ShapePoint fontSize={`${GIS_POINT_ICON_SIZE_PIXELS}px`} />;
  }
};

export { getShapesLayerGroupHistoric, getShapesLayerGroupRealTime };
